// Which `events` rows Kin may TELL HER ABOUT — issue #1696.
//
// ── Kasandra, 2026-09-23 ─────────────────────────────────────────────────────
//
//   "all the surfaces should handle dismissed the same"
//
// One rule, one place, every surface that describes her day back to her:
//
//   status = 'active'  AND  is_dismissed = false
//
// ── What she got before this file existed ────────────────────────────────────
//
// She taps the ✕ on something on her Today tab, which writes is_dismissed =
// true. Then Kin texted her about it at 7am, listed it when she asked what was
// on today in chat or over SMS, offered to help her prep for it, and planned a
// takeout night around it. Eight readers, six of them filtering only `status`.
// No error, nothing in Sentry, every dashboard fine — it reads as Kin not
// listening.
//
// ⚠️ The trap that hid it, worth knowing before trusting a grep here: the
// morning note's query SELECTED `is_dismissed` and its mapper silently dropped
// it. So the column appeared accounted for in review, reached nothing, and a
// grep for `is_dismissed` in dispatch-notifications returned hits — from a
// DIFFERENT function (the Pro gift read, which did filter correctly). That is
// how a PR body came to claim voice "matches the morning note" when the morning
// note was one of the broken ones.
//
// ── Why the two rules are SEPARATE functions ─────────────────────────────────
//
// "Not dismissed" and "not a to-do" have different audiences, and fusing them
// is how this drifts again. Every reader wants visibility. Only the surfaces
// that draw a SCHEDULE want the category rule — the meal planner reading her
// evenings and the gift heads-up reading upcoming birthdays both legitimately
// want to see a row with category 'task'. A single helper that applied both
// would force the wrong rule on half the callers, and the usual response to
// that is a second hand-rolled copy. Which is this issue.
//
// ── ⚠️ WHERE THIS MUST NOT BE USED ──────────────────────────────────────────
//
// Readers that de-duplicate or check existence, NOT readers that describe her
// day. Filtering those does real harm:
//
//   materialize-recurrences (both reads) — they find the latest materialized
//   occurrence of a recurrence rule. They deliberately do not filter `status`
//   either, and that file records why: requiring 'active' made a soft-deleted
//   occurrence look un-materialized, so the weekly cron INSERTED IT AGAIN, and
//   "turning on the cron would have resurrected every individually deleted
//   occurrence within a day." Adding a dismissal filter reproduces that bug in
//   a new column — she dismisses something and it comes back days later, which
//   is worse than the bug this file fixes.
//
//   kin-chat/createEventWrite.findDuplicate — asks "is this already on her
//   calendar" before writing. Whether a dismissed row should count as a
//   duplicate is a real product question and not this one.
//
// eventVisibilityCoverage.test.ts enforces both halves: every describing reader
// must call onlyVisibleEvents, and every exclusion must be listed with a reason.

/** The only `status` value that is on her calendar at all. */
export const EVENT_ACTIVE_STATUS = "active";

/**
 * `events.source_type` for a row written by `materialize-recurrences` — issue
 * #1780.
 *
 * ⚠️ SINGULAR. The TABLE is `recurrences`; the MARKER is `recurrence`. Writing
 * the plural matches zero rows, and a zero-row match is not an error here — the
 * series delete treats "no future instances materialized yet" as a legitimate
 * success — so the plural fails SILENTLY and reports "0 upcoming ones came off
 * her calendar" while leaving every one of them showing. Caught in #1780's own
 * first draft, which is why the value lives here now instead of being spelled
 * out at each site.
 *
 * Shared because the two halves of a series delete are in different functions:
 * `kin-chat/eventEditWrite.ts` decides a row IS a recurrence instance, and
 * `_shared/eventConfirmApply.ts` later clears the instances by this marker. They
 * have to mean the same thing or the first half proposes what the second cannot
 * find. `src/components/TodayTab.tsx` holds the app's copy (it cannot import
 * from an Edge Function) and `recurrenceMarkerHygiene.test.ts` pins it.
 */
export const RECURRENCE_SOURCE = "recurrence";

/**
 * PostgREST `or` filter for "this is a calendar entry, not a to-do".
 *
 * ⚠️ SEPARATE from visibility on purpose — see the header. `category` is
 * nullable and a null category IS an event (#1637 made that the read-side rule
 * for imports, and a manual row with no category has always drawn on the
 * schedule), which is why this cannot be a simple `.neq`.
 */
export const EVENT_NOT_A_TASK_FILTER = "category.is.null,category.neq.task";

/**
 * Apply "Kin may tell her about this" to an `events` query.
 *
 * ⚠️ A FUNCTION over the query builder rather than two constants to copy, and
 * that is the whole point of the file. Constants still leave eight call sites
 * each writing `.eq("status", X).eq("is_dismissed", false)` by hand, and one of
 * them forgetting the second line is exactly this issue. A wrapper cannot be
 * half-applied.
 *
 * Deliberately untyped in — every caller's client is a differently-shaped `any`
 * (three Edge Functions use their own PersonalSupabaseClient aliases), and a
 * real generic over PostgREST's builder types would not survive
 * `deno check` and `tsc` with strictNullChecks off. The coverage test is what
 * enforces use; the type system was never going to.
 */
export function onlyVisibleEvents<T>(query: T): T {
  return (query as any)
    .eq("status", EVENT_ACTIVE_STATUS)
    .eq("is_dismissed", false) as T;
}

/**
 * The same rule as a predicate, for a reader that already holds rows.
 *
 * ⚠️ Needs BOTH columns in the select list. A row read without them has
 * `undefined` there and is judged NOT visible, so this is only safe where both
 * were asked for — which is why the query wrapper above is the primary form and
 * this exists for `kinFeedRows.isFeedRow`, whose select does include them.
 *
 * ⚠️ `is_dismissed === false`, NOT `!is_dismissed` — Copilot caught this on
 * #1699 and it is the one place these two implementations could have disagreed.
 * Postgres `is_dismissed = false` does **not** match NULL, so the query wrapper
 * EXCLUDES a null-dismissed row while `!null` would have INCLUDED it. The whole
 * point of this file is that the two forms are the same rule, so they must agree
 * on every input, including the ones that cannot happen.
 *
 * And that one cannot happen today: `events.is_dismissed` is NOT NULL in the
 * live schema (`src/integrations/supabase/types.ts` types it `boolean`, not
 * `boolean | null`). `FeedSourceRow` nonetheless declares `boolean | null`, so
 * the type permits what the column does not — which is exactly the kind of gap
 * that stops being theoretical the day a row is synthesised rather than read.
 */
export function isEventVisible(row: {
  status?: string | null;
  is_dismissed?: boolean | null;
}): boolean {
  return row?.status === EVENT_ACTIVE_STATUS && row?.is_dismissed === false;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPLETION — issue #1706, and a DIFFERENT question from dismissal
// ─────────────────────────────────────────────────────────────────────────────
//
// #1696 deliberately left this alone: Kasandra ruled on dismissed, and the right
// answer for completed is not the same on every surface. #1706 is that decision,
// and this is where it is recorded.
//
// ⚠️ NOT part of onlyVisibleEvents, and that is the whole design. Dismissal is
// one rule every reader wants. Completion is a per-surface CHOICE, and three of
// the eight readers must not apply it at all — fusing them would force the wrong
// answer on those three, and the usual answer to that is a second hand-rolled
// copy, which is exactly how #1696 happened.
//
// ── The decision, per surface ────────────────────────────────────────────────
//
// EXCLUDE — surfaces that describe what is AHEAD of her. A briefing about the
// day to come has no business listing what is already done.
//   · the 7am morning note              (dispatch-notifications, the day read)
//   · prep offers                       (dispatch-notifications, the 3-day read)
//   · the meal plan's busy nights       (run-scheduled-tasks)
//
// MARK, DO NOT HIDE — surfaces ANSWERING A QUESTION SHE ASKED. At 4pm "you had
// the dentist at nine, that's done" is the true answer; silence about it is not.
// Hiding it would also make "did I already do that?" unanswerable.
//   · chat and every SMS reply          (kin-chat ambient block → " (done)")
//   · voice's get_schedule              (src/lib/voiceSchedule → `done: true`)
//
// UNCHANGED, on purpose —
//   · the gift heads-up (dispatch-notifications) reads a strictly FUTURE window
//     (`gt`, not `gte`) looking for birthdays. Completion is not a concept that
//     applies, and adding the column to that select buys nothing.
//   · the Apple calendar feed (kin-calendar-feed) is re-pulled by other calendar
//     apps for months. A past event that VANISHES out of a subscribed calendar is
//     worse than one that is simply still there, done.
//
// ── ⚠️ Why this is `not.is.true` and dismissal is `= false` ──────────────────
//
// The two columns fail in opposite directions, so they get opposite defaults.
//
// A dismissal filter that is too loose SHOWS her something she swiped away —
// annoying. A completion filter that is too TIGHT deletes a real event from her
// morning note — she plans her day around a text that is missing something. So
// this one is written to be wrong only in the harmless direction: it excludes a
// row only where completion is POSITIVELY asserted. `is_completed = false` would
// drop a NULL, and a NULL here means "nobody has said this is done".
//
// `events.is_completed` is NOT NULL DEFAULT false today
// (20260722120000_events_add_completion_state.sql), so no live row can take that
// branch. It is written this way for the day the column is widened, an outer join
// synthesises a row, or this rule is copied onto a table with looser columns —
// and because a guard whose safety depends on a constraint two files away is a
// guard nobody can check locally.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply "she has not already checked this off" to an `events` query.
 *
 * Only for the EXCLUDE surfaces above. A reader that answers a question she
 * asked should mark completed events instead — see `isEventCompleted`.
 */
export function excludeCompletedEvents<T>(query: T): T {
  // `is_completed=not.is.true` — matches false AND null, unlike `.eq(false)`.
  return (query as any).not("is_completed", "is", true) as T;
}

/**
 * The same rule as a predicate, for the MARK surfaces: is this row done?
 *
 * ⚠️ A POSITIVE question about a state that can be absent. `!row.is_completed`
 * would answer "done" for a row read without the column in its select list, and
 * this repo has shipped that mistake in both directions already — a column
 * missing from an explicit select comes back `undefined` with no error. Only an
 * explicit `true` is done, which is also what makes this agree with
 * `excludeCompletedEvents` on every input including the impossible ones.
 */
export function isEventCompleted(row: { is_completed?: boolean | null }): boolean {
  return row?.is_completed === true;
}
